import React, { useCallback } from "react"
import { CreateNewFolder, InfoOutlined, Send, WidthNormal, WidthWide } from "@mui/icons-material";
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import { ButtonWithIcon } from "@/common/components/Button/ButtonWithIcon.tsx";
import Download from "@mui/icons-material/Download";
import fetchInstance from "@/services/fetch.ts";
import { isMacOs } from "react-device-detect";
import { FlexBox } from "@/common/components/Box/FlexBox";
import useUniqueTabId from "@/common/hooks/useUniqueTabId.ts";
import { URLSearchParamKeys } from "@/enum/URLSearchParamKeys.ts";
import useCurrentTrack from "@/common/hooks/useCurrentTrack.ts";
import { useModalQueryState } from "@/common/hooks/nuqs/useModalQueryState.ts";
import { useSearchParamState } from "@/common/hooks/nuqs/useSearchParamState.ts";
import Upload from "@mui/icons-material/Upload";
import CenterFocusWeakRounded from "@mui/icons-material/CenterFocusWeakRounded";
import { useRouter } from "next/navigation";
import useDataGridApi from "@/common/hooks/useDataGridApi.ts";
import useCurrentExplorerItem from "@/common/hooks/useCurrentExplorerItem.ts";
import { AppHashTag } from "@/enum/AppHashTag.ts";
import Settings from "@mui/icons-material/Settings";
import EditIcon from "@mui/icons-material/Edit";
import { Divider, Theme, useMediaQuery } from "@mui/material";
import useAudioEditorVisible from "@/common/hooks/preferences/useAudioEditorVisible.ts";
import useExplorerFullscreen from "@/common/hooks/preferences/useExplorerFullscreen.ts";
import Box from "@mui/material/Box";
import { goToExplorerItem } from "@/features/Waveform/utils.ts";
import { usePlaylistTrackQueryState } from "@/common/hooks/nuqs/usePlaylistTrackQueryState.ts";

export const ExplorerToolsMenu = React.memo(() => {
  const currentTrack = useCurrentTrack()
  const [, setEditorIsOpen] = useAudioEditorVisible()
  const { setModalParam: setUploadModalParam } = useModalQueryState(URLSearchParamKeys.UPLOAD_MODAL);
  const { setModalParam: setShareTrackModalSearchParam } = useModalQueryState(URLSearchParamKeys.SHARE_TRACK_MODAL)
  const { setModalParam: setTrackInfoModalSearchParam } = useModalQueryState(URLSearchParamKeys.TRACK_INFO_MODAL)
  const { setModalParam: setCreatePlaylistModalSearchParam } = useModalQueryState(URLSearchParamKeys.CREATE_PLAYLIST_MODAL);
  const isMediumScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const isLargeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  const [explorerFullscreen, setExplorerFullscreen] = useExplorerFullscreen()
  const { setPlaylistAndTrackQueryState } = usePlaylistTrackQueryState();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [uniqueTabId] = useUniqueTabId()
  const router = useRouter();
  const [dataGridApi] = useDataGridApi();
  const currentExplorerItem = useCurrentExplorerItem();
  const [playlistSearchParam] = useSearchParamState(URLSearchParamKeys.PLAYLIST);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenUploadModal = () => {
    handleClose()
    setUploadModalParam(true)
  }

  const handleOpenCreateFolderModal = () => {
    handleClose()
    setCreatePlaylistModalSearchParam("1");
  }

  const handleDownloadTrack = () => {
    handleClose()

    fetchInstance
      .post(
        "/tracks/download",
        {
          explorerItem: [currentTrack?.explorerItemId],
          uniqueTabId
        },
      )
  }

  const handleScrollToRow = useCallback(() => {
    if (!currentExplorerItem || !dataGridApi?.current) {
      return;
    }

    goToExplorerItem(
      currentExplorerItem,
      playlistSearchParam,
      setPlaylistAndTrackQueryState,
      dataGridApi?.current,
      router,
    );
  }, [
    currentExplorerItem,
    dataGridApi,
    playlistSearchParam,
    router,
    setPlaylistAndTrackQueryState
  ]);

  const handleEditTrack = useCallback(() => {
    if (!currentExplorerItem) {
      return
    }
    handleClose()
    window.location.hash = AppHashTag.EDITOR
    setEditorIsOpen(true)
  }, [
    currentExplorerItem,
    setEditorIsOpen
  ])

  return (
    <>
      {
        <>
          <FlexBox sx={{ alignItems: 'center', textAlign: 'center' }}>
            <ButtonWithIcon
              tooltip={"Tools"}
              icon={<Settings />}
              onClick={handleClick}
              size="small"
              aria-controls={open ? 'tools-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
            />
          </FlexBox>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            // onClick={handleClose}
            slotProps={{
              paper: {
                elevation: 5,
                sx: {
                  minWidth: 250,
                  bgcolor: 'background.medium'
                }
              }
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleOpenUploadModal}>
              <ListItemIcon>
                <Upload fontSize="medium" />
              </ListItemIcon>
              <ListItemText>Upload</ListItemText>
            </MenuItem>

            <MenuItem onClick={handleOpenCreateFolderModal}>
              <ListItemIcon>
                <CreateNewFolder fontSize="medium" />
              </ListItemIcon>
              <ListItemText>Create folder</ListItemText>
            </MenuItem>

            {currentExplorerItem && <MenuItem onClick={handleEditTrack}>
              <ListItemIcon>
                <EditIcon fontSize="medium" />
              </ListItemIcon>
              <ListItemText>Edit track</ListItemText>
            </MenuItem>}

            {currentExplorerItem && <MenuItem onClick={() => {
              handleClose();
              handleScrollToRow();
            }}>
              <ListItemIcon>
                <CenterFocusWeakRounded fontSize="medium" />
              </ListItemIcon>
              <ListItemText>Go to track</ListItemText>
            </MenuItem>}

            <MenuItem onClick={handleDownloadTrack}>
              <ListItemIcon>
                <Download fontSize="medium" />
              </ListItemIcon>
              <ListItemText>Download track</ListItemText>
            </MenuItem>

            <MenuItem
              onClick={() => {
                setShareTrackModalSearchParam(currentTrack!.explorerItemId)
                handleClose()
              }}
            >
              <ListItemIcon>
                <Send fontSize="medium" />
              </ListItemIcon>
              <ListItemText>Share track</ListItemText>
            </MenuItem>

            <MenuItem
              onClick={() => {
                if (!currentTrack) {
                  return
                }
                setTrackInfoModalSearchParam(currentTrack?.explorerItemId)
                handleClose()
              }}>
              <ListItemIcon>
                <InfoOutlined />
              </ListItemIcon>
              <ListItemText>Track information</ListItemText>
              {!isMediumScreen && <Typography>
                {isMacOs ? 'CMD+[i]' : 'CTRL+[i]'}
              </Typography>}
            </MenuItem>

            {
              isLargeScreen &&
              <Box>
                <Divider />

                <MenuItem
                  onClick={() => {
                    handleClose();
                    setExplorerFullscreen(!explorerFullscreen);
                  }}>
                  <ListItemIcon>
                    {
                      explorerFullscreen
                        ? <WidthNormal />
                        : <WidthWide />
                    }
                  </ListItemIcon>
                  <ListItemText>
                    {
                      explorerFullscreen
                        ? <>Standard table view</>
                        : <>Wide table view</>
                    }
                  </ListItemText>
                </MenuItem>
              </Box>
            }

          </Menu>
        </>
      }
    </>
  );
});
